const SECTION_TRIGGER_CLASS = "section-scroll-trigger";
const ELEMENT_TRIGGER_CLASS = "scroll-trigger";
const ACTIVE_SECTION_CLASS = "section-active";
const OFFSCREEN_CLASS = "scroll-trigger--offscreen";
const CANCEL_CLASS = "scroll-trigger--cancel";
const DEFAULT_ROOT_MARGIN = "0px 0px -20% 0px";

/**
 * @param {Element} element
 * @param {number} viewportHeight
 * @returns {boolean}
 */
function isElementInViewport(element, viewportHeight) {
  const rect = element.getBoundingClientRect();
  return rect.bottom > 0 && rect.top < viewportHeight;
}

/**
 * @param {IntersectionObserverEntry[]} entries
 * @param {IntersectionObserver} observer
 */
function onSectionIntersection(entries, observer) {
  entries.forEach((entry) => {
    if (!entry.isIntersecting) return;

    const section = entry.target;
    section.classList.add(ACTIVE_SECTION_CLASS);
    observer.unobserve(section);
  });
}

/**
 * @param {IntersectionObserverEntry[]} entries
 * @param {IntersectionObserver} observer
 */
function onElementIntersection(entries, observer) {
  entries.forEach((entry, index) => {
    const element = entry.target;
    if (!(element instanceof HTMLElement)) return;

    if (entry.isIntersecting) {
      if (element.classList.contains(OFFSCREEN_CLASS)) {
        element.classList.remove(OFFSCREEN_CLASS);
        if (element.hasAttribute("data-cascade")) {
          element.style.setProperty("--animation-order", String(index));
        }
      }
      observer.unobserve(element);
      return;
    }

    element.classList.add(OFFSCREEN_CLASS);
    element.classList.remove(CANCEL_CLASS);
  });
}

/**
 * @param {Document | Element} [rootEl=document]
 * @param {boolean} [isDesignMode=false]
 */
function initializeScrollAnimations(rootEl = document, isDesignMode = false) {
  /** @type {Window & { Theme?: { settings?: { animationObserverRootMargin?: string } } }} */
  const windowWithTheme = window;
  const configuredRootMargin =
    windowWithTheme.Theme?.settings?.animationObserverRootMargin || DEFAULT_ROOT_MARGIN;

  const sections = Array.from(rootEl.getElementsByClassName(SECTION_TRIGGER_CLASS));
  const allScrollTriggers = Array.from(rootEl.getElementsByClassName(ELEMENT_TRIGGER_CLASS));
  // Descendants of a section trigger use the section-active CSS path only; observing them
  // separately adds scroll-trigger--offscreen and fights the parent reveal.
  const elements = allScrollTriggers.filter((element) => {
    if (!(element instanceof HTMLElement)) return false;
    return !element.closest(`.${SECTION_TRIGGER_CLASS}`);
  });

  if (!sections.length && !elements.length) return;

  if (isDesignMode) {
    sections.forEach((section) => section.classList.add("scroll-trigger--design-mode"));
    elements.forEach((element) => element.classList.add("scroll-trigger--design-mode"));
    return;
  }

  if (sections.length) {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const sectionObserver = new IntersectionObserver(onSectionIntersection, {
      rootMargin: configuredRootMargin,
      threshold: 0.1,
    });
    sections.forEach((section) => {
      if (isElementInViewport(section, viewportHeight)) {
        section.classList.add(ACTIVE_SECTION_CLASS);
        return;
      }
      sectionObserver.observe(section);
    });
  }

  if (elements.length) {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const elementObserver = new IntersectionObserver(onElementIntersection, {
      rootMargin: configuredRootMargin,
    });
    elements.forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      if (isElementInViewport(element, viewportHeight)) {
        element.classList.remove(OFFSCREEN_CLASS);
        element.classList.remove(CANCEL_CLASS);
        return;
      }
      element.classList.add(OFFSCREEN_CLASS);
      elementObserver.observe(element);
    });
  }
}

window.addEventListener("DOMContentLoaded", () => {
  initializeScrollAnimations();
});

if (window.Shopify && Shopify.designMode) {
  document.addEventListener("shopify:section:load", (event) => {
    if (event.target instanceof Element || event.target instanceof Document) {
      initializeScrollAnimations(event.target, true);
    }
  });
  document.addEventListener("shopify:section:reorder", () => {
    initializeScrollAnimations(document, true);
  });
}
